import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  LayoutDashboard, FileText, ListChecks, MessageSquare, Building2,
  LogOut, Menu, BookOpen, FileOutput, Settings as SettingsIcon,
  Factory, BarChart2, Calendar, BarChart, Users, HelpCircle as HelpIcon,
  Shield, ShieldAlert, Wind, FileSpreadsheet, Camera, Calculator, FlaskConical, ExternalLink, Keyboard, Mail, X, Search,
  Leaf, Award, Target, TrendingDown
} from 'lucide-react';

import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Settings from './components/Settings';
import Dashboard from './components/Dashboard';
import Facilities from './components/Facilities';
import DocumentUpload from './components/DocumentUpload';
import PromptManagement from './components/admin/PromptManagement';
import AdminPanel from './components/admin/AdminPanel';
import Tasks from './components/Tasks';
import Chat from './components/Chat';
import KnowledgeBase from './components/KnowledgeBase';
import DocumentGenerator from './components/DocumentGenerator';
import Sources from './components/Sources';
import Audit from './components/Audit';
import Reporting from './components/Reporting';
import Reports from './components/Reports';
import Contacts from './components/Contacts';
import GHGDashboard from './components/GHGDashboard';
import GHGInventory from './components/GHGInventory';
import GHGTargets from './components/GHGTargets';
import GHGCredits from './components/GHGCredits';
import GHGSettings from './components/GHGSettings';
import GHGReductions from './components/GHGReductions';
import TankEmissions from './components/TankEmissions';
import RMPScreening from './components/RMPScreening';
import AERScreen from './components/AERScreen';
import PermitDeconstruct from './components/PermitDeconstruct';
import RegulatoryVersions from './components/RegulatoryVersions';
import Docs from './components/Docs';
import FederalStandards from './components/FederalStandards';
import PhotoAnalysis from './components/PhotoAnalysis';
import EmissionFactors from './components/EmissionFactors';
import BactLaer from './components/BactLaer';
import GlobalSearch from './components/GlobalSearch';

function CollapsibleSection({ label, children, defaultOpen = true, collapsed, isMobile }) {
  const [open, setOpen] = React.useState(defaultOpen);
  if (collapsed && !isMobile) return <>{children}</>;
  return (
    <div style={{ marginTop: 4 }}>
      <div onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px 4px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'var(--font-body)' }}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s', flexShrink: 0, color: '#9CA3AF' }}>
          <path d="M1 3L5 7L9 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, onClick, collapsed, isMobile }) {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/dashboard' && to !== '/ghg' && location.pathname.startsWith(to + '/')) || (to !== '/dashboard' && to !== '/ghg' && location.pathname === to);
  return (
    <Link to={to} onClick={onClick} className={`nav-item ${isActive ? 'active' : ''} ${collapsed && !isMobile ? 'nav-item-collapsed' : ''}`} title={collapsed ? label : undefined}>
      <Icon size={16} style={{ flexShrink: 0 }} />
      <span style={{
        maxWidth: (collapsed && !isMobile) ? 0 : 200,
        opacity: (collapsed && !isMobile) ? 0 : 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        transition: 'max-width 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.2s cubic-bezier(0.4,0,0.2,1)',
        display: 'inline-block',
      }}>{label}</span>
    </Link>
  );
}

function HelpMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 7, color: open ? '#0066FF' : '#374151', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500 }}>
        <HelpIcon size={15} color={open ? '#0066FF' : '#374151'} />
        <span>Help center</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', zIndex: 1000, width: 220, overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>Help & Resources</div>
          </div>
          <button onClick={() => { navigate('/docs'); setOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <BookOpen size={14} color="#0066FF" /> Documentation
          </button>
          <button onClick={() => setOpen(false)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <Keyboard size={14} color="#0066FF" /> Keyboard Shortcuts
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', background: '#F1F5F9', padding: '1px 6px', borderRadius: 4 }}>⌘K</span>
          </button>
          <div style={{ borderTop: '1px solid #F1F5F9' }}>
            <a href="mailto:support@aircomply.com" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', fontSize: 13, color: '#374151', textDecoration: 'none', fontFamily: 'var(--font-body)' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <Mail size={14} color="#0066FF" /> Contact Support
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, onLogout, showName = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const initials = user?.fullName
    ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (user?.email?.[0] || 'A').toUpperCase();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: '#EFF6FF', border: open ? '2px solid #0066FF' : '2px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: 13, fontWeight: 800, color: '#0066FF', flexShrink: 0 }}>
          {initials}
        </div>
        {showName && (
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', fontFamily: 'var(--font-body)', lineHeight: 1.3 }}>{user?.fullName?.split(' ')[0] || user?.email}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontFamily: 'var(--font-body)', lineHeight: 1.2 }}>Account Owner</div>
          </div>
        )}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, background: 'white', border: '1px solid #E2E8F0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', zIndex: 1000, width: 220, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #F1F5F9' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0A1628' }}>{user?.fullName || user?.email}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{user?.companyName || 'AirComply'}</div>
          </div>
          <button onClick={() => { navigate('/settings'); setOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <SettingsIcon size={14} color="#64748b" /> Settings
          </button>
          {user?.role === 'admin' && (
            <button onClick={() => { navigate('/admin'); setOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', fontFamily: 'var(--font-body)' }}
              onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <Shield size={14} color="#64748b" /> Admin Panel
            </button>
          )}
          <div style={{ borderTop: '1px solid #F1F5F9' }}>
            <button onClick={() => { onLogout(); setOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#DC2626', fontFamily: 'var(--font-body)' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse, isMobile }) {
  return (
    <>
      {isOpen && <div className="mobile-overlay" onClick={onClose} />}
      <aside className={`sidebar ${isOpen ? 'open' : ''} ${collapsed && !isMobile ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-nav">

          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/facilities" icon={Building2} label="Facilities" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/sources" icon={Factory} label="Sources" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/documents/upload" icon={FileText} label="Documents" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/tasks" icon={ListChecks} label="Tasks" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/reporting" icon={Calendar} label="Reporting" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/audit" icon={BarChart2} label="Audit" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/contacts" icon={Users} label="Contacts" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <div className="nav-divider" style={{ display: collapsed ? 'block' : 'none' }} />
          <CollapsibleSection label="Tools" defaultOpen={true} collapsed={collapsed} isMobile={isMobile}>
            <NavItem to="/chat/new" icon={MessageSquare} label="AI Chat" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/bact-laer" icon={FlaskConical} label="BACT / LAER / RACT" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/photo-analysis" icon={Camera} label="Photo Analysis" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/documents/generator" icon={FileOutput} label="Generate Docs" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/federal-standards" icon={Shield} label="Federal Standards" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/emission-factors" icon={Calculator} label="Emission Factors" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/tank-emissions" icon={FlaskConical} label="Tank Emissions" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/rmp-screening" icon={ShieldAlert} label="RMP Screening" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/aerscreen" icon={Wind} label="Dispersion Screening" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          <NavItem to="/permit-deconstruct" icon={FileSpreadsheet} label="Permit Deconstruct" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/knowledge-base" icon={BookOpen} label="Knowledge Base" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/reports" icon={BarChart} label="Reports" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          </CollapsibleSection>
          <CollapsibleSection label="Carbon" defaultOpen={true} collapsed={collapsed} isMobile={isMobile}>
            <NavItem to="/ghg" icon={Leaf} label="GHG Dashboard" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/ghg/inventory" icon={BarChart} label="Emissions Inventory" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/ghg/targets" icon={Target} label="Reduction Targets" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/ghg/credits" icon={Award} label="Carbon Credits" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/ghg/reductions" icon={TrendingDown} label="Reduction Strategies" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
            <NavItem to="/ghg/settings" icon={Calculator} label="GHG Settings" onClick={onClose} collapsed={collapsed} isMobile={isMobile} />
          </CollapsibleSection>
        </div>
      </aside>
    </>
  );
}

function Topbar({ user, onLogout, onMenuClick, collapsed, onToggleCollapse, isMobile }) {
  const initials = user?.fullName
    ? user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (user?.email?.[0] || 'A').toUpperCase();

  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <header className="app-topbar">
      {/* Mobile search expanded */}
      {mobileSearchOpen && (
        <div style={{ position: 'absolute', inset: 0, background: 'white', zIndex: 100, display: 'flex', alignItems: 'center', padding: '0 12px', gap: 8 }}>
          <GlobalSearch />
          <button onClick={() => setMobileSearchOpen(false)} className="icon-btn" style={{ flexShrink: 0 }}>
            <X size={15} />
          </button>
        </div>
      )}

      {/* Logo */}
      <div className="app-topbar-logo" style={{ width: collapsed && !isMobile ? 60 : 220, borderRight: '1px solid var(--color-sidebar-border)', transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)', padding: collapsed && !isMobile ? '0' : '0 20px', alignItems: 'center', display: 'flex', position: 'relative', overflow: 'hidden' }}
        onMouseEnter={e => {
          if (!collapsed || isMobile) return;
          const logo = e.currentTarget.querySelector('.logo-content');
          const btn = e.currentTarget.querySelector('.topbar-collapse-btn');
          if(logo) logo.style.opacity = '0';
          if(btn) btn.style.opacity = '1';
        }}
        onMouseLeave={e => {
          if (!collapsed || isMobile) return;
          const logo = e.currentTarget.querySelector('.logo-content');
          const btn = e.currentTarget.querySelector('.topbar-collapse-btn');
          if(logo) logo.style.opacity = '1';
          if(btn) btn.style.opacity = '0';
        }}>
        {/* When collapsed on desktop — shield absolutely centered */}
        {collapsed && !isMobile ? (
          <Link to="/dashboard" className="logo-content" style={{ textDecoration: 'none', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s' }}>
            <svg width="22" height="26" viewBox="-1 -1 34 40" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1 L31 6 L31 21 Q31 33 16 37 Q1 33 1 21 L1 6 Z" fill="white" stroke="#0A1628" strokeWidth="2"/>
              <path d="M6 13 Q11 9 16 12 Q21 15 26 11" stroke="#0A1628" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
              <path d="M6 18 Q11 14 16 17 Q21 20 26 16" stroke="#0066FF" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
              <path d="M6 23 Q11 19 16 22 Q21 25 26 21" stroke="#0066FF" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.6"/>
              <path d="M7 28 Q11 24 16 27 Q21 30 25 26" stroke="#0066FF" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.3"/>
            </svg>
          </Link>
        ) : (
          <Link to="/dashboard" className="logo-content" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, flex: 1, transition: 'opacity 0.2s' }}>
            <svg width="22" height="26" viewBox="-1 -1 34 40" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path d="M16 1 L31 6 L31 21 Q31 33 16 37 Q1 33 1 21 L1 6 Z" fill="white" stroke="#0A1628" strokeWidth="2"/>
              <path d="M6 13 Q11 9 16 12 Q21 15 26 11" stroke="#0A1628" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
              <path d="M6 18 Q11 14 16 17 Q21 20 26 16" stroke="#0066FF" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
              <path d="M6 23 Q11 19 16 22 Q21 25 26 21" stroke="#0066FF" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.6"/>
              <path d="M7 28 Q11 24 16 27 Q21 30 25 26" stroke="#0066FF" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.3"/>
            </svg>
            <span className="logo-text" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, letterSpacing: '-0.8px', whiteSpace: 'nowrap' }}>
              <span style={{ color: '#0066FF' }}>Air</span><span style={{ color: '#0A1628' }}>Comply</span>
            </span>
          </Link>
        )}
        {/* Collapse button — always visible in expanded mode */}
        {!collapsed && (
          <button onClick={onToggleCollapse} style={{ width: 24, height: 24, borderRadius: 6, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94a3b8', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        {/* Expand arrow — only rendered when collapsed, fades in on hover */}
        {collapsed && (
          <div className="topbar-collapse-btn" onClick={e => {
            const logo = e.currentTarget.closest('.app-topbar-logo').querySelector('.logo-content');
            if(logo) logo.style.opacity = '1';
            onToggleCollapse();
          }} style={{ opacity: 0, transition: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'rgba(248,250,252,0.95)' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'white', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ transform: 'rotate(180deg)' }}>
                <path d="M9 2L4 7L9 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Search — center of content area */}
      <div className="app-topbar-search">
        <GlobalSearch />
      </div>

      {/* Right actions */}
      <div className="app-topbar-right">
        <HelpMenu />
        <div style={{ width: 1, height: 20, background: '#E2E8F0', flexShrink: 0 }} />
        <UserMenu user={user} onLogout={onLogout} showName={true} />
      </div>

      {/* Mobile: search + user + hamburger */}
      <div className="app-topbar-mobile-right">
        <button onClick={() => setMobileSearchOpen(true)} className="icon-btn">
          <Search size={15} />
        </button>
        <UserMenu user={user} onLogout={onLogout} showName={false} />
        <button onClick={onMenuClick} className="icon-btn">
          <Menu size={16} />
        </button>
      </div>
    </header>
  );
}

const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours
const WARNING_BEFORE = 5 * 60 * 1000; // warn 5 mins before

function AppLayout({ children, user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const resetTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    setShowTimeoutWarning(false);
    // Warning at 55 mins
    warningRef.current = setTimeout(() => setShowTimeoutWarning(true), INACTIVITY_TIMEOUT - WARNING_BEFORE);
    // Logout at 60 mins
    timeoutRef.current = setTimeout(() => {
      onLogout();
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => resetTimer();
    events.forEach(e => window.addEventListener(e, handleActivity));
    resetTimer(); // start timer on mount
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [resetTimer]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapse = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebarCollapsed', !prev);
      return !prev;
    });
  };

  return (
    <div className="app-layout">
      {showTimeoutWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏱️</div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Session Expiring Soon</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 20, lineHeight: 1.6 }}>You'll be automatically logged out in 5 minutes due to inactivity.</div>
            <button onClick={resetTimer} style={{ width: '100%', padding: '11px', background: '#0066FF', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              Keep me logged in
            </button>
          </div>
        </div>
      )}
      <Topbar user={user} onLogout={onLogout} onMenuClick={() => setSidebarOpen(!sidebarOpen)} collapsed={collapsed} onToggleCollapse={toggleCollapse} isMobile={isMobile} />
      <div className={`app-body ${collapsed ? 'app-body-collapsed' : ''}`}>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={collapsed} onToggleCollapse={toggleCollapse} isMobile={isMobile} />
        <main className="main-content">
          <div className="page-content">{children}</div>
        </main>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const PrivateRoute = ({ children }) => {
    if (loading) return null;
    if (!isAuthenticated) return <Navigate to="/login" />;
    return <AppLayout user={user} onLogout={handleLogout}>{children}</AppLayout>;
  };

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login setAuth={setIsAuthenticated} setUser={setUser} />} />
        <Route path="/register" element={<Register setAuth={setIsAuthenticated} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/facilities" element={<PrivateRoute><Facilities /></PrivateRoute>} />
        <Route path="/sources" element={<PrivateRoute><Sources /></PrivateRoute>} />
        <Route path="/federal-standards" element={<PrivateRoute><FederalStandards /></PrivateRoute>} />
        <Route path="/photo-analysis" element={<PrivateRoute><PhotoAnalysis /></PrivateRoute>} />
        <Route path="/emission-factors" element={<PrivateRoute><EmissionFactors /></PrivateRoute>} />
        <Route path="/bact-laer" element={<PrivateRoute><BactLaer /></PrivateRoute>} />
        <Route path="/documents" element={<Navigate to="/documents/upload" replace />} />
        <Route path="/documents/upload" element={<PrivateRoute><DocumentUpload /></PrivateRoute>} />
        <Route path="/tasks" element={<PrivateRoute><Tasks /></PrivateRoute>} />
        <Route path="/reporting" element={<PrivateRoute><Reporting /></PrivateRoute>} />
        <Route path="/audit" element={<PrivateRoute><Audit /></PrivateRoute>} />
        <Route path="/chat/:conversationId" element={<PrivateRoute><Chat /></PrivateRoute>} />
        <Route path="/knowledge-base" element={<PrivateRoute><KnowledgeBase /></PrivateRoute>} />
        <Route path="/documents/generator" element={<PrivateRoute><DocumentGenerator /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
        <Route path="/contacts" element={<PrivateRoute><Contacts /></PrivateRoute>} />
        <Route path="/ghg" element={<PrivateRoute><GHGDashboard /></PrivateRoute>} />
        <Route path="/ghg/inventory" element={<PrivateRoute><GHGInventory /></PrivateRoute>} />
        <Route path="/ghg/targets" element={<PrivateRoute><GHGTargets /></PrivateRoute>} />
        <Route path="/ghg/credits" element={<PrivateRoute><GHGCredits /></PrivateRoute>} />
        <Route path="/ghg/reductions" element={<PrivateRoute><GHGReductions /></PrivateRoute>} />
        <Route path="/ghg/settings" element={<PrivateRoute><GHGSettings /></PrivateRoute>} />
        <Route path="/tank-emissions" element={<PrivateRoute><TankEmissions /></PrivateRoute>} />
        <Route path="/rmp-screening" element={<PrivateRoute><RMPScreening /></PrivateRoute>} />
        <Route path="/aerscreen" element={<PrivateRoute><AERScreen /></PrivateRoute>} />
        <Route path="/permit-deconstruct" element={<PrivateRoute><PermitDeconstruct /></PrivateRoute>} />
        <Route path="/regulatory-versions" element={<PrivateRoute><RegulatoryVersions /></PrivateRoute>} />
        <Route path="/docs" element={<PrivateRoute><Docs /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/admin/prompts" element={<PrivateRoute><PromptManagement /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />
        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
      </Routes>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover
        toastStyle={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, borderRadius: 10 }}
      />
    </Router>
  );
}

export default App;
